/**
 * Error Fallback UI Component
 * Displays a user-friendly error message with recovery options
 */

import React from "react";
import { PixelWarningIcon } from "./PixelIcons";

export interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
  /**
   * Custom title for the error display
   */
  title?: string;
  /**
   * Custom message override
   */
  message?: string;
  /**
   * Show details toggle
   */
  showDetails?: boolean;
  /**
   * Additional recovery actions
   */
  actions?: React.ReactNode;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetErrorBoundary,
  title = "Something went wrong",
  message,
  showDetails = false,
  actions,
}) => {
  const [showErrorDetails, setShowErrorDetails] = React.useState(showDetails);

  const errorMessage =
    message || error.message || "An unexpected error occurred";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background text-text-primary p-4">
      <div className="bg-surface-secondary p-6 border-2 border-error rounded-xl shadow-pixel-shadow max-w-lg w-full">
        <div className="flex items-center mb-4">
          <div className="text-error mr-2">
            <PixelWarningIcon />
          </div>
          <h2 className="text-error text-xl font-pixel">{title}</h2>
        </div>

        <p className="text-text-primary mb-4 text-sm leading-relaxed">
          {errorMessage}
        </p>

        {showErrorDetails && (
          <div className="mb-4 p-3 bg-error/10 border border-error/30 rounded text-xs font-mono text-text-secondary overflow-auto max-h-48">
            <div className="font-semibold mb-1">Error Details:</div>
            <div className="break-words">{error.stack || error.toString()}</div>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <button
            onClick={resetErrorBoundary}
            className="bg-primary text-surface py-2 px-4 rounded font-pixel shadow-pixel-shadow hover:bg-primary-hover active:scale-95 transition-transform text-sm"
          >
            Try Again
          </button>

          <button
            onClick={() => setShowErrorDetails(!showErrorDetails)}
            className="text-text-secondary hover:text-text-primary text-xs underline"
          >
            {showErrorDetails ? "Hide" : "Show"} Error Details
          </button>

          {actions && <div className="mt-2">{actions}</div>}
        </div>

        <div className="mt-4 pt-4 border-t border-border/50 text-xs text-text-secondary">
          <p>
            If this problem persists, please try refreshing the page or contact
            support.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ErrorFallback;
